import { Loader } from '@googlemaps/js-api-loader';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { useEffect, useState } from 'react';
import RedesignModal from '../../../shared/components/RedesignModal';
import locale from '../locale.json';
import { useRecrutementModalStyles } from './RecrutementModal.styles';
import RecrutementForm from './RecrutementForm';

type RecrutementModalProps = {
  open: boolean;
  user: Record<string, any>;
  onClose: () => void;
  getUser: () => void;
  preventClose?: boolean;
  idEbook?: string | number;
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
};

const RecrutementModal = ({
  open,
  user,
  onClose,
  getUser,
  preventClose = false,
  idEbook,
  secondaryAction,
}: RecrutementModalProps) => {
  const classes = useRecrutementModalStyles();
  const [isFormReady, setIsFormReady] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!open) {
      setIsFormReady(false);
      setLoadError(false);
      return undefined;
    }

    let cancelled = false;

    const prepareGoogleMaps = async () => {
      const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
      if (!apiKey) {
        if (!cancelled) setLoadError(true);
        return;
      }

      try {
        if (!window.google?.maps?.places) {
          await new Loader({
            apiKey,
            libraries: ['places'],
            language: 'FR',
            region: 'FR',
          }).load();
        }
        if (!cancelled) setIsFormReady(true);
      } catch {
        if (!cancelled) setLoadError(true);
      }
    };

    prepareGoogleMaps();

    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  const handleOverlayClose = () => {
    if (!preventClose) {
      onClose();
    }
  };

  return (
    <RedesignModal
      open={open}
      onClose={handleOverlayClose}
      labelledBy="recrutement-modal-heading"
      paperClassName={classes.modal}
      zIndex={1304}
      usePortal={true}>
      <div className={classes.headerBlock}>
        <div className={classes.headerRow}>
          <h2 id="recrutement-modal-heading" className={classes.modalHeading}>
            {locale.recrutementModalHeading}
          </h2>
          {!preventClose && (
            <IconButton className={classes.closeButton} onClick={onClose} aria-label="Fermer">
              <CloseIcon />
            </IconButton>
          )}
          {secondaryAction && (
            <button
              type="button"
              className={classes.secondaryAction}
              onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </button>
          )}
        </div>
        <p className={classes.modalDescription}>{locale.recrutementModalDescription}</p>
      </div>

      {loadError && <p className={classes.errorMessage}>{locale.googleMapsError}</p>}

      {!isFormReady && !loadError && (
        <div className={classes.loadingWrap}>
          <CircularProgress size={32} />
        </div>
      )}

      {isFormReady && (
        <RecrutementForm user={user} onClose={onClose} getUser={getUser} idEbook={idEbook} />
      )}
    </RedesignModal>
  );
};

export default RecrutementModal;
